import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { useEffect } from 'react';
import { useChatStore } from '@/hooks/useChatStore';
import { colors } from '@/theme/colors';
import ExploreSectionHeading from '@/pages/explore/ExploreSectionHeading';

type props = {
  type: 'Performance' | 'Marketing' | 'Transactions';
  handleSuggestionClick: (s: string) => void;
  isStreaming: boolean;
};
const performanceSuggestions = [
  'What is my current revenue share contribution?',
  'How close am I to reaching my next cap?',
  'What is my new transaction volume this year?',
  'How much have I earned in commissions YTD?',
];
const ExploreLayout: React.FC<props> = ({
  type,
  handleSuggestionClick,
  isStreaming,
}) => {
  const { setError, setLoading, clearChatHistory } = useChatStore();
  const theme = useTheme();
  useEffect(() => {
    setError(null);
    setLoading(true);
    clearChatHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <Box
      data-testid="explore-layout"
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        flexDirection: 'column',
        gap: '2rem',
        marginY: 'auto',
        width: '100%',
      }}
    >
      <ExploreSectionHeading
        type={type}
        data-testid="explore-section-heading"
      />
      <Box
        data-testid="chat-suggestions"
        sx={{
          display: 'grid',
          alignContent: 'center',
          gridTemplateColumns: {
            xs: 'repeat(4, 1fr)',
            sm: 'repeat(4, 1fr)',
          },
          pb: 2,
          p: 3,
          gap: '10px',
          maxWidth: {
            xs: '100%',
            sm: '100%',
            md: '100%',
            lg: '90%',
          },
          padding: '10px',
          overflowX: 'auto',
          scrollbarWidth: 'thin',
          '&::-webkit-scrollbar': {
            height: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: theme.palette.background.default,
          },
          '&::-webkit-scrollbar-thumb': {
            background: theme.palette.primary.main,
          },
        }}
      >
        {performanceSuggestions.map((s) => (
          <Button
            key={s}
            data-testid={`chat-suggestion-${s.replace(/\s+/g, '-').toLowerCase()}`}
            sx={{
              backgroundColor: theme.palette.background.paper,
              borderRadius: '8px',
              minWidth: '190px',
              padding: '8px 16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              boxShadow: '0px 2px 5px rgba(0, 0, 0, 0.1)',
              fontSize: '10px',
              fontWeight: '300',
              textTransform: 'none',
              color: theme.palette.text.primary,
              transition:
                'background-color 0.2s ease-in-out, transform 0.1s ease-in-out',
              '&:hover': {
                color: colors.black,
                backgroundColor: colors.mistGray,
                transform: 'scale(1.02)',
              },
            }}
            disabled={isStreaming}
            onClick={() => handleSuggestionClick(s)}
          >
            <Typography
              data-testid={`suggestion-text-${s.replace(/\s+/g, '-').toLowerCase()}`}
              sx={{
                fontSize: '14px',
                lineHeight: '16px',
                textAlign: 'left',
                whiteSpace: 'normal',
                overflow: 'auto',
                wordBreak: 'break-word',
                maxHeight: '32px',
                '&::-webkit-scrollbar': {
                  width: '3px',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: theme.palette.grey[500],
                  borderRadius: '2px',
                },
              }}
            >
              {s}
            </Typography>
          </Button>
        ))}
      </Box>
    </Box>
  );
};

export default ExploreLayout;
